<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import ExcelJS from 'exceljs'
import { del, get, patch, post } from '@/api'
import { useClientStore } from '@/stores/client'
import { clientItemsApi, clientsApi, targetItemMapApi, targetsApi } from '@/api/entities'
import { targetActionsApi } from '@/api/entities/review/targetActions'
import { targetItemMapActionsApi } from '@/api/entities/review/targetItemMapActions'
import { uploadHistoryApi } from '@/api/entities/review/uploadHistory'
import { getErrorMessage } from '@/utils/error'
import { clientNameEquals } from '@/utils/review/clientNameComparator'
import ArchiveUploadHistoryDialog from '@/components/review/ArchiveUploadHistoryDialog.vue'

definePage({
  meta: {
    title: '매핑 관리',
    action: 'read',
    subject: 'DriverConfig',
  },
})

const { snackbar, handleError } = useErrorHandler()

const MSG_MAPPING_DELETE_FAILED = '매핑 삭제에 실패했습니다.'
const MSG_PRODUCT_NAME_COLLECT_FAILED = '상품명 수집 요청에 실패했습니다.'
const MSG_SIMILARITY_CALC_FAILED = '유사도 계산 요청에 실패했습니다.'
const MSG_REVIEW_COLLECT_FAILED = '리뷰 수집 요청에 실패했습니다.'
const MSG_PRODUCT_NAME_RESET_SUCCESS = '상품명이 초기화되었습니다.'
const MSG_DIRECT_DELIVERY_DELETE_SUCCESS = '직진배송 상품이 삭제되었습니다.'
const MSG_PRODUCT_NAME_BULK_RESET_FAILED = '상품명 일괄 초기화에 실패했습니다.'
const MSG_DIRECT_DELIVERY_BULK_DELETE_FAILED = '직진배송 일괄 삭제에 실패했습니다.'

type TargetTitleScanResponse = Awaited<ReturnType<typeof targetsApi.resolveProductNames>>

const notifyTargetTitleScanResult = (response: TargetTitleScanResponse, successMessage: string) => {
  if (!response.success) {
    handleError(response.error, { notificationMessage: getErrorMessage(response.error, MSG_PRODUCT_NAME_COLLECT_FAILED) })

    return
  }

  const result = response.data
  if (!result?.success) {
    snackbar.warning(result?.message || MSG_PRODUCT_NAME_COLLECT_FAILED)

    return
  }

  const totalCount = result.total_count > 0 ? ` (${result.total_count}개 채널 설정)` : ''

  snackbar.success(`${successMessage}${totalCount}`)
}

// 스토어
const clientStore = useClientStore()
const selectedClient = computed(() => clientStore.selectedClient)

// 인터페이스
interface SortItem {
  key: string
  order?: 'asc' | 'desc'
}

interface DriverConfig {
  id: number
  name: string
  download_driver: {
    id: number
    name: string
  }
  collected_review_count: number | null
  filtered_review_count: number | null
}

interface TargetItemMap {
  id: number
  config_id: number
  client_id: number
  target_id: number | null
  driver_name: string
  target_item_code: string
  target_item_name: string | null
  target_item_name_resolved: number | null
  target_item_url: string | null
  client_item_code: string
  client_item_name: string | null
  client_item_name_resolved: number | null
  client_item_url: string | null
  similarity: number | null
  collected_review_count: number | null
  filtered_review_count: number | null
  duplicate_client_items: number | null
  duplicate_target_items: number | null
}

// 상태
const loading = ref(false)
const mappings = ref<TargetItemMap[]>([])
const totalMapCount = ref(0)
const page = ref(1)
const itemsPerPage = ref(25)
const search = ref('')
const sortBy = ref<SortItem[]>([{ key: 'id', order: 'desc' }])
const selectedMappings = ref<TargetItemMap[]>([])
const driverConfigs = ref<DriverConfig[]>([])
const selectedDriverConfigId = ref<number | null>(null)

// 통계
const clientCollectedReviewsCount = ref<number | null>(null)
const clientFilteredReviewsCount = ref<number | null>(null)

// 엑셀 다운로드 로딩
const isExcelDownloading = ref(false)

const targetActionLoading = ref<string | null>(null)

// 다이얼로그
const confirmDialog = ref(false)
const confirmMessage = ref('')
const confirmAction = ref<(() => void | Promise<void>) | null>(null)

// 매핑 수정 다이얼로그
const editDialog = ref(false)

const mappingForEdit = reactive({
  id: null as number | null,
  target_item_code: '',
  client_item_code: '',
})

// 매핑 추가 다이얼로그
const createDialog = ref(false)

const mappingsForCreate = ref([
  { config_id: null as number | null, target_item_code: '', client_item_code: '' },
])

// 테이블 헤더
const headers = [
  { title: 'ID', key: 'id', sortable: true, width: '80px' },
  { title: '채널', key: 'driver_name', sortable: true },
  { title: '채널 상품', key: 'target_item_name' },
  { title: '자사몰 상품', key: 'client_item_name' },
  { title: '유사도', key: 'similarity', sortable: true, width: '100px' },
  { title: '수집된 리뷰', key: 'collected_review_count', sortable: true, width: '120px' },
  { title: '이관 예상', key: 'filtered_review_count', sortable: true, width: '120px' },
  { title: '자사몰 중복', key: 'duplicate_client_items', sortable: true, width: '100px' },
  { title: '채널 중복', key: 'duplicate_target_items', sortable: true, width: '100px' },
  { title: '', key: 'actions', sortable: false, width: '60px' },
]

// 드라이버 설정 옵션
const driverConfigOptions = computed(() => {
  const options: { title: string; value: number | null }[] = [{ title: '전체 채널', value: null }]

  driverConfigs.value.forEach(config => {
    options.push({
      title: config.download_driver?.name || config.name,
      value: config.id,
    })
  })

  return options
})

// 추가 가능한 채널 목록
const addableChannels = computed(() => {
  return driverConfigs.value.map(config => ({
    title: config.download_driver?.name || config.name,
    value: config.id,
  }))
})

// 채널별 리뷰 수
const reviewCountByChannels = computed(() => {
  return driverConfigs.value.map(config => ({
    channel_name: config.download_driver?.name || config.name,
    collected_review_count: config.collected_review_count,
    filtered_review_count: config.filtered_review_count,
  }))
})

// 숫자 포맷
const formatNumber = (num: number | null) => {
  if (num === null || num === undefined)
    return '-'

  return num.toLocaleString()
}

// 확인 다이얼로그
const showConfirm = (message: string, action: () => void | Promise<void>) => {
  confirmMessage.value = message
  confirmAction.value = action
  confirmDialog.value = true
}

const confirmActionExecuting = ref(false)

const executeConfirmAction = async () => {
  if (confirmActionExecuting.value || !confirmAction.value)
    return

  confirmActionExecuting.value = true
  try {
    await confirmAction.value()
  }
  finally {
    confirmActionExecuting.value = false
  }

  confirmDialog.value = false
}

// 드라이버 설정 로드
const loadDriverConfigs = async () => {
  if (!selectedClient.value?.id) {
    driverConfigs.value = []

    return
  }

  try {
    const { success, data } = await get<{ data: DriverConfig[] }>(
      `/review/clients/${selectedClient.value.id}/driver-configs`,
      { per_page: 100 },
    )

    if (success && data)
      driverConfigs.value = data.data || []
  }
  catch (error) {
    handleError(error, { silent: true })
  }
}

// 고객사 통계 응답 타입
interface ClientStats {
  collected_review_count: number | null
  filtered_review_count: number | null
}

// 고객사 통계 로드
const loadClientStats = async () => {
  if (!selectedClient.value?.id) {
    clientCollectedReviewsCount.value = null
    clientFilteredReviewsCount.value = null

    return
  }

  try {
    const { success, data } = await get<{ data: ClientStats } | ClientStats>(`/review/clients/${selectedClient.value.id}`)
    if (success && data) {
      // Laravel JsonResource가 { data: { ... } } 형태로 wrapping할 수 있음
      const clientData = (data as { data?: ClientStats }).data || data as ClientStats

      clientCollectedReviewsCount.value = clientData.collected_review_count ?? null
      clientFilteredReviewsCount.value = clientData.filtered_review_count ?? null
    }
  }
  catch (error) {
    handleError(error, { silent: true })
  }
}

// 매핑 목록 로드
const loadMappings = async () => {
  if (!selectedClient.value?.id) {
    mappings.value = []
    totalMapCount.value = 0

    return
  }

  loading.value = true
  try {
    const sortKey = sortBy.value[0]?.key || 'id'
    const sortOrder = sortBy.value[0]?.order || 'desc'

    const params: Record<string, string | number> = {
      page: page.value,
      per_page: itemsPerPage.value,
      sort: sortKey,
      order: sortOrder,
    }

    if (search.value)
      params.search = search.value

    if (selectedDriverConfigId.value)
      params.driver_config_id = selectedDriverConfigId.value

    const { success, data } = await get<{ data: TargetItemMap[]; total: number }>(
      `/review/clients/${selectedClient.value.id}/target-item-map`,
      params,
    )

    if (success && data) {
      mappings.value = data.data || []
      totalMapCount.value = data.total || 0
    }
  }
  catch (error) {
    handleError(error, { notificationMessage: '매핑 목록을 불러오는데 실패했습니다.' })
  }
  finally {
    loading.value = false
  }
}

// 매핑 삭제
const deleteMapping = (mapping: TargetItemMap) => {
  showConfirm(`${mapping.id} 매핑을 삭제하시겠습니까?`, async () => {
    try {
      const { success } = await del(
        `/review/clients/${mapping.client_id}/target-item-map/${mapping.id}`,
      )

      if (success) {
        snackbar.success('매핑을 삭제했습니다.')
        await Promise.all([
          loadMappings(),
          loadClientStats(),
          loadDriverConfigs(),
        ])
      }
      else {
        snackbar.error(MSG_MAPPING_DELETE_FAILED)
      }
    }
    catch (error) {
      handleError(error, { notificationMessage: MSG_MAPPING_DELETE_FAILED })
    }
  })
}

// 매핑 수정 다이얼로그 열기
const openEditDialog = (mapping: TargetItemMap) => {
  mappingForEdit.id = mapping.id
  mappingForEdit.target_item_code = mapping.target_item_code
  mappingForEdit.client_item_code = mapping.client_item_code
  editDialog.value = true
}

// 매핑 수정
const updateMapping = async () => {
  if (!selectedClient.value?.id || !mappingForEdit.id)
    return

  try {
    const { success } = await patch(
      `/review/clients/${selectedClient.value.id}/target-item-map/${mappingForEdit.id}`,
      {
        target_item_code: mappingForEdit.target_item_code,
        client_item_code: mappingForEdit.client_item_code,
      },
    )

    if (success) {
      snackbar.success('매핑을 수정했습니다.')
      editDialog.value = false
      await Promise.all([
        loadMappings(),
        loadClientStats(),
        loadDriverConfigs(),
      ])
    }
  }
  catch (error) {
    handleError(error, { notificationMessage: '매핑 수정에 실패했습니다.' })
  }
}

// 매핑 추가 다이얼로그 열기
const openCreateDialog = () => {
  mappingsForCreate.value = [
    { config_id: null, target_item_code: '', client_item_code: '' },
  ]
  createDialog.value = true
}

// 매핑 추가 행 추가
const addCreateMappingInput = () => {
  const lastMapping = mappingsForCreate.value[mappingsForCreate.value.length - 1]

  mappingsForCreate.value.push({
    config_id: lastMapping.config_id,
    target_item_code: '',
    client_item_code: '',
  })
}

// 매핑 추가 행 제거
const removeCreateMappingInput = (index: number) => {
  if (mappingsForCreate.value.length > 1)
    mappingsForCreate.value.splice(index, 1)
}

// 매핑 저장
const storeMapping = async () => {
  if (!selectedClient.value?.id)
    return

  const validMappings = mappingsForCreate.value.filter(
    m => m.config_id && m.target_item_code && m.client_item_code,
  )

  if (validMappings.length === 0) {
    snackbar.warning('유효한 매핑 정보를 입력해주세요.')

    return
  }

  try {
    const { success } = await post(
      `/review/clients/${selectedClient.value.id}/target-item-map`,
      { mappings: validMappings },
    )

    if (success) {
      snackbar.success('매핑을 추가했습니다.')
      createDialog.value = false
      await Promise.all([
        loadMappings(),
        loadClientStats(),
        loadDriverConfigs(),
      ])
    }
  }
  catch (error) {
    handleError(error, { notificationMessage: '매핑 추가에 실패했습니다.' })
  }
}

// 전체 매핑 삭제 — 업체명 직접 타이핑 확인 + 사유(선택) 다이얼로그
const deleteAllDialog = ref(false)
const deleteAllConfirmName = ref('')
const deleteAllReason = ref('')
const isDeletingAll = ref(false)

// 백엔드 ClientNameComparator 와 동일 정규화 사용 — NBSP/em space/전각 공백
// 우회를 막고, 동시에 서버가 허용하는 입력은 프런트도 허용하도록 대칭성 보장.
const canConfirmDeleteAll = computed(() =>
  !isDeletingAll.value
  && !!selectedClient.value
  && clientNameEquals(deleteAllConfirmName.value, selectedClient.value.name),
)

const openDeleteAllDialog = () => {
  if (!selectedClient.value?.id)
    return
  deleteAllConfirmName.value = ''
  deleteAllReason.value = ''
  deleteAllDialog.value = true
}

const confirmDeleteAllMappings = async () => {
  if (!canConfirmDeleteAll.value || !selectedClient.value)
    return

  isDeletingAll.value = true
  try {
    const response = await targetItemMapApi.deleteTargetItemMapsForClient(
      selectedClient.value.id,
      deleteAllConfirmName.value.trim(),
      deleteAllReason.value || undefined,
    )

    if (response.success && response.data) {
      const deletedCount = response.data.deletedCount
      if (deletedCount === 0) {
        // 0건이지만 정상 종료 — 사용자에게 명시적으로 알린다
        snackbar.warning('삭제 대상 매핑이 없었습니다. (조건이 빈 범위였을 수 있음)')
      }
      else {
        snackbar.success(`${deletedCount}건의 매핑을 삭제했습니다. (감사 ID: ${response.data.requestId})`)
      }
      deleteAllDialog.value = false
      await Promise.all([
        loadMappings(),
        loadClientStats(),
        loadDriverConfigs(),
      ])
    }
    else {
      // del() 은 4xx/5xx 도 throw 하지 않고 success:false 로 swallow 한다.
      // 서버가 보낸 422 message ("업체명 확인이 일치하지 않습니다." 등) 를
      // 사용자에게 그대로 노출해 silent failure 를 막는다.
      snackbar.error(getErrorMessage(response.error, MSG_MAPPING_DELETE_FAILED))
    }
  }
  catch (error) {
    handleError(error, { notificationMessage: MSG_MAPPING_DELETE_FAILED })
  }
  finally {
    isDeletingAll.value = false
  }
}

// 선택된 매핑만 삭제 — 사유(선택) + 단순 confirm 다이얼로그
const deleteSelectedDialog = ref(false)
const deleteSelectedReason = ref('')
const isDeletingSelected = ref(false)

const openDeleteSelectedDialog = () => {
  if (!selectedClient.value?.id || selectedMappings.value.length === 0)
    return
  deleteSelectedReason.value = ''
  deleteSelectedDialog.value = true
}

const confirmDeleteSelectedMappings = async () => {
  if (isDeletingSelected.value || !selectedClient.value)
    return
  if (selectedMappings.value.length === 0) {
    snackbar.error('선택된 매핑이 없습니다.')
    
    return
  }

  isDeletingSelected.value = true
  try {
    const ids = selectedMappings.value.map(m => m.id)

    const response = await targetItemMapApi.deleteSelectedTargetItemMaps(
      selectedClient.value.id,
      ids,
      deleteSelectedReason.value || undefined,
    )

    if (response.success && response.data) {
      const deletedCount = response.data.deletedCount
      if (deletedCount === 0) {
        snackbar.warning('삭제 대상 매핑이 없었습니다. (이미 삭제되었거나 조건 변경)')
      }
      else {
        snackbar.success(`선택된 매핑 ${deletedCount}건을 삭제했습니다. (감사 ID: ${response.data.requestId})`)
      }
      deleteSelectedDialog.value = false
      selectedMappings.value = []
      await Promise.all([
        loadMappings(),
        loadClientStats(),
        loadDriverConfigs(),
      ])
    }
    else {
      // 서버 422 메시지("일부 매핑이 해당 업체/드라이버 범위에 속하지 않습니다." 등)
      // 를 그대로 노출해 silent failure 를 막는다.
      snackbar.error(getErrorMessage(response.error, '선택된 매핑 삭제에 실패했습니다.'))
    }
  }
  catch (error) {
    handleError(error, { notificationMessage: '선택된 매핑 삭제에 실패했습니다.' })
  }
  finally {
    isDeletingSelected.value = false
  }
}

// 상품명 수집 - 전체 자사몰
const resolveClientProductNames = () => {
  if (!selectedClient.value?.id)
    return

  showConfirm('전체 매핑의 자사몰 상품명 수집을 요청하시겠습니까?', async () => {
    try {
      const { success } = await clientItemsApi.resolveProductNames(selectedClient.value!.id)
      if (success)
        snackbar.success('자사몰 상품명 수집이 요청되었습니다. 완료 시 슬랙으로 알려드립니다.')
    }
    catch (error) {
      handleError(error, { notificationMessage: MSG_PRODUCT_NAME_COLLECT_FAILED })
    }
  })
}

// 상품명 수집 - 전체 채널
const resolveTargetProductNames = () => {
  if (!selectedClient.value?.id)
    return

  showConfirm('전체 매핑의 채널 상품명 수집을 요청하시겠습니까?', async () => {
    try {
      const response = await targetsApi.resolveProductNames(selectedClient.value!.id)

      notifyTargetTitleScanResult(response, '채널 상품명 수집이 요청되었습니다. 완료 시 슬랙으로 알려드립니다.')
    }
    catch (error) {
      handleError(error, { notificationMessage: MSG_PRODUCT_NAME_COLLECT_FAILED })
    }
  })
}

// 상품명 수집 - 선택된 매핑 자사몰
const resolveClientProductNamesForMappings = () => {
  if (!selectedClient.value?.id || selectedMappings.value.length === 0)
    return

  showConfirm('선택된 매핑의 자사몰 상품명 수집을 요청하시겠습니까?', async () => {
    try {
      const mappingIds = selectedMappings.value.map(m => m.id)

      const { success } = await clientItemsApi.resolveProductNamesByTargetItemMapIds(
        selectedClient.value!.id,
        mappingIds,
      )

      if (success)
        snackbar.success('선택된 매핑의 자사몰 상품명 수집이 요청되었습니다.')
    }
    catch (error) {
      handleError(error, { notificationMessage: MSG_PRODUCT_NAME_COLLECT_FAILED })
    }
  })
}

// 자사몰 상품명 초기화 - 전체
const resetClientProductNames = () => {
  if (!selectedClient.value?.id)
    return

  showConfirm('전체 매핑의 자사몰 상품명을 초기화하시겠습니까? (item_name=NULL, is_name_checked=0)', async () => {
    try {
      const response = await clientItemsApi.resetProductNames(selectedClient.value!.id)
      if (response.success) {
        const resetCount = (response.data as { reset_count?: number } | undefined)?.reset_count ?? 0

        snackbar.success(`자사몰 상품명 ${resetCount}건 초기화됨.`)
      }
      else {
        snackbar.error('자사몰 상품명 초기화에 실패했습니다.')
      }
    }
    catch (error) {
      handleError(error, { notificationMessage: '자사몰 상품명 초기화에 실패했습니다.' })
    }
  })
}

// 자사몰 상품명 초기화 - 선택된 매핑
const resetClientProductNamesForMappings = () => {
  if (!selectedClient.value?.id || selectedMappings.value.length === 0)
    return

  showConfirm(`선택된 ${selectedMappings.value.length}건 매핑의 자사몰 상품명을 초기화하시겠습니까?`, async () => {
    try {
      const mappingIds = selectedMappings.value.map(m => m.id)
      const response = await clientItemsApi.resetProductNamesByTargetItemMapIds(selectedClient.value!.id, mappingIds)
      if (response.success) {
        const resetCount = (response.data as { reset_count?: number } | undefined)?.reset_count ?? 0

        snackbar.success(`자사몰 상품명 ${resetCount}건 초기화됨.`)
      }
      else {
        snackbar.error('자사몰 상품명 초기화에 실패했습니다.')
      }
    }
    catch (error) {
      handleError(error, { notificationMessage: '자사몰 상품명 초기화에 실패했습니다.' })
    }
  })
}

// 자사몰 상품명 강제 수집 - 전체
const forceResolveClientProductNames = () => {
  if (!selectedClient.value?.id)
    return

  showConfirm('전체 매핑의 자사몰 상품명 강제 수집을 요청하시겠습니까? (기존 값 덮어씀)', async () => {
    try {
      const { success } = await clientItemsApi.resolveProductNames(selectedClient.value!.id, { force: true })
      if (success)
        snackbar.success('자사몰 상품명 강제 수집이 요청되었습니다. 완료 시 슬랙으로 알려드립니다.')
      else
        snackbar.error('자사몰 상품명 강제 수집 요청에 실패했습니다.')
    }
    catch (error) {
      handleError(error, { notificationMessage: MSG_PRODUCT_NAME_COLLECT_FAILED })
    }
  })
}

// 자사몰 상품명 강제 수집 - 선택된 매핑
const forceResolveClientProductNamesForMappings = () => {
  if (!selectedClient.value?.id || selectedMappings.value.length === 0)
    return

  showConfirm(`선택된 ${selectedMappings.value.length}건 매핑의 자사몰 상품명 강제 수집을 요청하시겠습니까? (기존 값 덮어씀)`, async () => {
    try {
      const mappingIds = selectedMappings.value.map(m => m.id)
      const { success } = await clientItemsApi.resolveProductNamesByTargetItemMapIds(selectedClient.value!.id, mappingIds, { force: true })
      if (success)
        snackbar.success('선택된 매핑의 자사몰 상품명 강제 수집이 요청되었습니다.')
      else
        snackbar.error('자사몰 상품명 강제 수집 요청에 실패했습니다.')
    }
    catch (error) {
      handleError(error, { notificationMessage: MSG_PRODUCT_NAME_COLLECT_FAILED })
    }
  })
}

// 상품명 수집 - 선택된 매핑 채널
const resolveTargetProductNamesForMappings = () => {
  if (!selectedClient.value?.id || selectedMappings.value.length === 0)
    return

  showConfirm('선택된 매핑의 채널 상품명 수집을 요청하시겠습니까?', async () => {
    try {
      const mappingIds = selectedMappings.value.map(m => m.id)

      const response = await targetsApi.resolveProductNamesByTargetItemMapIds(
        selectedClient.value!.id,
        mappingIds,
      )

      notifyTargetTitleScanResult(response, '선택된 매핑의 채널 상품명 수집이 요청되었습니다.')
    }
    catch (error) {
      handleError(error, { notificationMessage: MSG_PRODUCT_NAME_COLLECT_FAILED })
    }
  })
}

// 상품명 수집 - 특정 채널
const resolveTargetProductNameByDriverConfigId = (configId: number, driverName: string) => {
  if (!selectedClient.value?.id)
    return

  showConfirm(`${driverName}의 상품명 수집을 요청하시겠습니까?`, async () => {
    try {
      const { success } = await targetsApi.resolveProductNameByDriverConfigId(
        selectedClient.value!.id,
        configId,
      )

      if (success)
        snackbar.success(`${driverName}의 상품명 수집이 요청되었습니다.`)
    }
    catch (error) {
      handleError(error, { notificationMessage: MSG_PRODUCT_NAME_COLLECT_FAILED })
    }
  })
}

// 상품명 수집 - 단일 매핑 (채널)
const resolveTargetProductNameById = (mappingId: number) => {
  if (!selectedClient.value?.id)
    return

  showConfirm(`채널 상품 코드 ${mappingId}의 상품명 수집을 요청하시겠습니까?`, async () => {
    try {
      const { success } = await targetsApi.resolveProductNamesByTargetItemMapIds(
        selectedClient.value!.id,
        [mappingId],
      )

      if (success)
        snackbar.success(`채널 상품 코드 ${mappingId}의 상품명 수집이 요청되었습니다.`)
    }
    catch (error) {
      handleError(error, { notificationMessage: MSG_PRODUCT_NAME_COLLECT_FAILED })
    }
  })
}

// 상품명 수집 - 단일 매핑 (자사몰)
const resolveClientProductNameById = (mappingId: number) => {
  if (!selectedClient.value?.id)
    return

  showConfirm(`자사몰 상품 코드 ${mappingId}의 상품명 수집을 요청하시겠습니까?`, async () => {
    try {
      const { success } = await clientItemsApi.resolveProductNamesByTargetItemMapIds(
        selectedClient.value!.id,
        [mappingId],
      )

      if (success)
        snackbar.success(`자사몰 상품 코드 ${mappingId}의 상품명 수집이 요청되었습니다.`)
    }
    catch (error) {
      handleError(error, { notificationMessage: MSG_PRODUCT_NAME_COLLECT_FAILED })
    }
  })
}

// 유사도 생성 - 전체
const buildSimilarity = () => {
  if (!selectedClient.value?.id)
    return

  showConfirm('전체 매핑의 유사도 계산을 요청하시겠습니까?', async () => {
    try {
      const { success } = await targetItemMapApi.buildSimilarity(selectedClient.value!.id)
      if (success) {
        snackbar.success('유사도 계산이 요청되었습니다. 완료 시 슬랙으로 알려드립니다.')
        await loadMappings()
      }
    }
    catch (error) {
      handleError(error, { notificationMessage: MSG_SIMILARITY_CALC_FAILED })
    }
  })
}

// 유사도 생성 - 선택된 매핑
const buildSimilarityForMappings = () => {
  if (!selectedClient.value?.id || selectedMappings.value.length === 0)
    return

  showConfirm('선택된 매핑의 유사도 계산을 요청하시겠습니까?', async () => {
    try {
      const mappingIds = selectedMappings.value.map(m => m.id)

      const { success } = await targetItemMapApi.buildSimilarityForMappings(
        selectedClient.value!.id,
        mappingIds,
      )

      if (success)
        snackbar.success('선택된 매핑의 유사도 계산이 요청되었습니다.')
    }
    catch (error) {
      handleError(error, { notificationMessage: MSG_SIMILARITY_CALC_FAILED })
    }
  })
}

// 리뷰 수집 응답 타입
interface CollectReviewsResponse {
  is_reserved?: boolean
  state?: 'queued' | 'running' | string
  requestId?: string
}

// 리뷰 수집 실행 (공통)
const executeCollectReviews = async (flagForce = false) => {
  try {
    const { success, data } = await clientsApi.collectReviews(selectedClient.value!.id, { flagForce })
    if (success) {
      const responseData = data as CollectReviewsResponse | undefined
      if (responseData?.state === 'queued' || responseData?.is_reserved)
        snackbar.success('리뷰 수집 요청이 대기열에 등록되었습니다. 실제 실행 및 완료 시 슬랙으로 알려드립니다.')
      else
        snackbar.warning('동일한 요청이 이미 존재합니다.')
    }
  }
  catch (error) {
    handleError(error, { notificationMessage: MSG_REVIEW_COLLECT_FAILED })
  }
}

// 리뷰 수집 - 전체
const collectReviews = async () => {
  if (!selectedClient.value?.id)
    return

  try {
    const { data: status } = await clientsApi.checkCollectStatus(selectedClient.value.id)

    if (status && status.collected_count > 0) {
      const msg = status.already_collected
        ? `오늘 이미 전체 ${status.total_count}건의 수집이 완료되었습니다. 강제로 재수집하시겠습니까?`
        : `오늘 이미 ${status.collected_count}/${status.total_count}건이 수집되었습니다. 강제로 재수집하시겠습니까?`

      showConfirm(msg, () => executeCollectReviews(true))

      return
    }
  }
  catch {
    // 상태 체크 실패 시 기존 동작으로 진행
  }

  showConfirm('전체 매핑의 리뷰 수집을 시작하시겠습니까?', () => executeCollectReviews())
}

// 리뷰 수집 매핑 실행 (공통)
const executeCollectForMappings = async (mappingIds: number[], flagForce = false) => {
  try {
    const { success } = await clientsApi.collectReviewsForMappings(
      selectedClient.value!.id,
      mappingIds,
      { flagForce },
    )

    if (success)
      snackbar.success('선택된 매핑의 리뷰 수집이 시작되었습니다.')
  }
  catch (error) {
    handleError(error, { notificationMessage: MSG_REVIEW_COLLECT_FAILED })
  }
}

// 리뷰 수집 - 선택된 매핑
// 매핑별 수집은 클라이언트 전체 사전 체크를 하지 않음 (워커의 last_crawled_date 체크가 안전장치)
const collectReviewsForMappings = async () => {
  if (!selectedClient.value?.id || selectedMappings.value.length === 0)
    return

  // 유효한 mapping ID만 필터링 (null, undefined, 0 이하 값 제외)
  const mappingIds = selectedMappings.value
    .map(m => m.id)
    .filter((id): id is number => id != null && id > 0)

  if (mappingIds.length === 0) {
    snackbar.warning('유효한 매핑이 없습니다.')

    return
  }

  showConfirm('선택된 매핑의 리뷰 수집을 시작하시겠습니까?', () => executeCollectForMappings(mappingIds))
}

// 리뷰 수집 - 단일 매핑
const collectReviewsForMapping = (mappingId: number) => {
  if (!selectedClient.value?.id)
    return

  showConfirm('해당 매핑의 리뷰 수집을 시작하시겠습니까?', () => executeCollectForMappings([mappingId]))
}

// 상품명 초기화
const clearItemTitle = (mapping: TargetItemMap) => {
  if (!mapping.target_id || targetActionLoading.value !== null)
    return

  showConfirm(`Target ${mapping.target_id}의 상품명을 초기화하시겠습니까?`, async () => {
    targetActionLoading.value = 'clearItemTitle'
    try {
      const { success } = await targetActionsApi.clearItemTitle(mapping.target_id!)
      if (success) {
        snackbar.success(MSG_PRODUCT_NAME_RESET_SUCCESS)
        await Promise.all([
          loadMappings(),
          loadClientStats(),
          loadDriverConfigs(),
        ])
      }
      else {
        snackbar.error('상품명 초기화에 실패했습니다.')
      }
    }
    catch (error) {
      handleError(error, { notificationMessage: '상품명 초기화에 실패했습니다.' })
    }
    finally {
      targetActionLoading.value = null
    }
  })
}

// 직진배송 삭제
const deleteZzExpress = (mapping: TargetItemMap) => {
  if (!mapping.target_id || targetActionLoading.value !== null)
    return

  showConfirm(`Target ${mapping.target_id}의 직진배송 상품을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`, async () => {
    targetActionLoading.value = 'deleteZzExpress'
    try {
      const { success } = await targetActionsApi.deleteZzExpress(mapping.target_id!)
      if (success) {
        snackbar.success(MSG_DIRECT_DELIVERY_DELETE_SUCCESS)
        await Promise.all([
          loadMappings(),
          loadClientStats(),
          loadDriverConfigs(),
        ])
      }
      else {
        snackbar.error('직진배송 상품 삭제에 실패했습니다.')
      }
    }
    catch (error) {
      handleError(error, { notificationMessage: '직진배송 삭제에 실패했습니다.' })
    }
    finally {
      targetActionLoading.value = null
    }
  })
}

// 전체 매핑 상품명 초기화
const clearItemTitleAll = () => {
  if (!selectedClient.value?.id || targetActionLoading.value !== null)
    return

  showConfirm('전체 매핑의 상품명을 초기화하시겠습니까?', async () => {
    targetActionLoading.value = 'bulkClearItemTitle'
    try {
      const { success, data } = await targetActionsApi.bulkClearItemTitle(selectedClient.value!.id)
      if (success) {
        snackbar.success((data as { message?: string })?.message || MSG_PRODUCT_NAME_RESET_SUCCESS)
        await Promise.all([
          loadMappings(),
          loadClientStats(),
          loadDriverConfigs(),
        ])
      }
      else {
        snackbar.error(MSG_PRODUCT_NAME_BULK_RESET_FAILED)
      }
    }
    catch (error) {
      handleError(error, { notificationMessage: MSG_PRODUCT_NAME_BULK_RESET_FAILED })
    }
    finally {
      targetActionLoading.value = null
    }
  })
}

// 선택된 매핑 상품명 초기화
const clearItemTitleForMappings = () => {
  if (!selectedClient.value?.id || selectedMappings.value.length === 0 || targetActionLoading.value !== null)
    return

  const targetIds = selectedMappings.value
    .map(m => m.target_id)
    .filter((id): id is number => id != null && id > 0)

  if (targetIds.length === 0) {
    snackbar.warning('선택된 매핑에 유효한 Target이 없습니다.')

    return
  }

  showConfirm(`선택된 매핑(${selectedMappings.value.length})의 상품명을 초기화하시겠습니까?`, async () => {
    targetActionLoading.value = 'bulkClearItemTitle'
    try {
      const { success, data } = await targetActionsApi.bulkClearItemTitle(selectedClient.value!.id, targetIds)
      if (success) {
        snackbar.success((data as { message?: string })?.message || MSG_PRODUCT_NAME_RESET_SUCCESS)
        await Promise.all([
          loadMappings(),
          loadClientStats(),
          loadDriverConfigs(),
        ])
      }
      else {
        snackbar.error(MSG_PRODUCT_NAME_BULK_RESET_FAILED)
      }
    }
    catch (error) {
      handleError(error, { notificationMessage: MSG_PRODUCT_NAME_BULK_RESET_FAILED })
    }
    finally {
      targetActionLoading.value = null
    }
  })
}

// 전체 매핑 직진배송 삭제
const deleteZzExpressAll = () => {
  if (!selectedClient.value?.id || targetActionLoading.value !== null)
    return

  showConfirm('전체 매핑의 직진배송 상품을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.', async () => {
    targetActionLoading.value = 'bulkDeleteZzExpress'
    try {
      const { success, data } = await targetActionsApi.bulkDeleteZzExpress(selectedClient.value!.id)
      if (success) {
        snackbar.success((data as { message?: string })?.message || MSG_DIRECT_DELIVERY_DELETE_SUCCESS)
        await Promise.all([
          loadMappings(),
          loadClientStats(),
          loadDriverConfigs(),
        ])
      }
      else {
        snackbar.error(MSG_DIRECT_DELIVERY_BULK_DELETE_FAILED)
      }
    }
    catch (error) {
      handleError(error, { notificationMessage: MSG_DIRECT_DELIVERY_BULK_DELETE_FAILED })
    }
    finally {
      targetActionLoading.value = null
    }
  })
}

// 선택된 매핑 직진배송 삭제
const deleteZzExpressForMappings = () => {
  if (!selectedClient.value?.id || selectedMappings.value.length === 0 || targetActionLoading.value !== null)
    return

  const targetIds = selectedMappings.value
    .map(m => m.target_id)
    .filter((id): id is number => id != null && id > 0)

  if (targetIds.length === 0) {
    snackbar.warning('선택된 매핑에 유효한 Target이 없습니다.')

    return
  }

  showConfirm(`선택된 매핑(${selectedMappings.value.length})의 직진배송 상품을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`, async () => {
    targetActionLoading.value = 'bulkDeleteZzExpress'
    try {
      const { success, data } = await targetActionsApi.bulkDeleteZzExpress(selectedClient.value!.id, targetIds)
      if (success) {
        snackbar.success((data as { message?: string })?.message || MSG_DIRECT_DELIVERY_DELETE_SUCCESS)
        await Promise.all([
          loadMappings(),
          loadClientStats(),
          loadDriverConfigs(),
        ])
      }
      else {
        snackbar.error(MSG_DIRECT_DELIVERY_BULK_DELETE_FAILED)
      }
    }
    catch (error) {
      handleError(error, { notificationMessage: MSG_DIRECT_DELIVERY_BULK_DELETE_FAILED })
    }
    finally {
      targetActionLoading.value = null
    }
  })
}

// 채널별 리뷰 수집
const collectReviewsByDriverConfig = (configId: number, driverName: string) => {
  showConfirm(`${driverName} 채널의 리뷰 수집을 시작하시겠습니까?`, async () => {
    try {
      const { success } = await clientsApi.collectReviewsByDriverConfig(configId)
      if (success)
        snackbar.success(`${driverName} 채널의 리뷰 수집이 시작되었습니다.`)
      else
        snackbar.error(MSG_REVIEW_COLLECT_FAILED)
    }
    catch (error) {
      handleError(error, { notificationMessage: MSG_REVIEW_COLLECT_FAILED })
    }
  })
}

// 유사도 계산 (단일)
const generateSimilarityForMapping = (mapping: TargetItemMap) => {
  if (targetActionLoading.value !== null)
    return

  showConfirm(`매핑 ${mapping.id}의 유사도를 계산하시겠습니까?`, async () => {
    targetActionLoading.value = 'generateSimilarity'
    try {
      const { success } = await targetItemMapActionsApi.generateSimilarity(mapping.id)
      if (success) {
        snackbar.success('유사도 계산이 요청되었습니다.')
        await Promise.all([
          loadMappings(),
          loadClientStats(),
          loadDriverConfigs(),
        ])
      }
      else {
        snackbar.error(MSG_SIMILARITY_CALC_FAILED)
      }
    }
    catch (error) {
      handleError(error, { notificationMessage: MSG_SIMILARITY_CALC_FAILED })
    }
    finally {
      targetActionLoading.value = null
    }
  })
}

// 리뷰 수 계산
const updateExpectedReviewCount = () => {
  if (!selectedClient.value?.id)
    return

  showConfirm('리뷰 수 계산을 요청하시겠습니까?', async () => {
    try {
      const { success } = await clientsApi.updateExpectedReviewCount(selectedClient.value!.id)
      if (success)
        snackbar.success('리뷰 수 계산이 요청되었습니다. 완료 시 슬랙으로 알려드립니다.')
    }
    catch (error) {
      handleError(error, { notificationMessage: '리뷰 수 계산 요청에 실패했습니다.' })
    }
  })
}

// 엑셀 다운로드
const downloadExcelExport = async () => {
  if (!selectedClient.value?.id)
    return

  isExcelDownloading.value = true
  try {
    const params: Record<string, string | number> = {
      page: 1,
      per_page: 100000,
      sort: 'id',
      order: 'asc',
    }

    if (selectedDriverConfigId.value)
      params.driver_config_id = selectedDriverConfigId.value

    const { success, data } = await get<{ data: TargetItemMap[] }>(
      `/review/clients/${selectedClient.value.id}/target-item-map`,
      params,
    )

    if (success && data) {
      const workbook = new ExcelJS.Workbook()

      const excelColumns = [
        { header: 'ID', key: 'id' },
        { header: '채널', key: 'driver_name' },
        { header: '채널 상품명', key: 'target_item_name' },
        { header: '채널 상품 코드', key: 'target_item_code' },
        { header: '자사몰 상품명', key: 'client_item_name' },
        { header: '자사몰 상품 코드', key: 'client_item_code' },
        { header: '유사도', key: 'similarity' },
        { header: '수집된 리뷰 수', key: 'collected_review_count' },
        { header: '이관 대상 리뷰 수', key: 'filtered_review_count' },
        { header: '자사몰 중복 건수', key: 'duplicate_client_items' },
        { header: '채널 중복 건수', key: 'duplicate_target_items' },
      ]

      driverConfigs.value.forEach(driverConfig => {
        if (selectedDriverConfigId.value && selectedDriverConfigId.value !== driverConfig.id)
          return

        const worksheet = workbook.addWorksheet(driverConfig.download_driver?.name || driverConfig.name)

        worksheet.columns = excelColumns

        const filteredData = data.data.filter(item => item.config_id === driverConfig.id)

        filteredData.forEach(row => {
          worksheet.addRow(row)
        })
      })

      const buffer = await workbook.xlsx.writeBuffer()

      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')

      link.href = url
      link.download = `${selectedClient.value.name}.xlsx`
      link.click()
      window.URL.revokeObjectURL(url)
    }
  }
  catch (error) {
    handleError(error, { notificationMessage: '엑셀 다운로드에 실패했습니다.' })
  }
  finally {
    isExcelDownloading.value = false
  }
}

// 페이지네이션 핸들러
const handlePageChange = (newPage: number) => {
  page.value = newPage
  loadMappings()
}

// 정렬 핸들러
const handleSortChange = (newSortBy: SortItem[]) => {
  sortBy.value = newSortBy
  loadMappings()
}

// 고객사 변경 감시
watch(selectedClient, async () => {
  page.value = 1
  selectedMappings.value = []
  selectedDriverConfigId.value = null
  driverConfigs.value = []
  await Promise.all([
    loadDriverConfigs(),
    loadClientStats(),
    loadMappings(),
  ])
}, { immediate: true })

// 채널 필터 변경 감시
watch(selectedDriverConfigId, () => {
  page.value = 1
  loadMappings()
})

// 검색 디바운스
let searchTimeout: ReturnType<typeof setTimeout> | null = null
watch(search, () => {
  if (searchTimeout)
    clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    page.value = 1
    loadMappings()
  }, 500)
})

// ───────────────────────────────────────────────────────────
// 이관 이력 삭제 (Danger Zone)
// 선택된 매핑 (최대 10개) 의 review_upload_target_list 레코드를
// review_upload_target_list_archive 로 이동. 서버가 chunk (500) 단위로
// 응답하므로 프론트는 `has_more` 가 false 가 될 때까지 재호출한다.
// ───────────────────────────────────────────────────────────
const archiveDialog = ref(false)
const archiveDialogRef = ref<InstanceType<typeof ArchiveUploadHistoryDialog> | null>(null)
const archiveRunning = ref(false)
const archiveCancelled = ref(false)

type ArchiveStatus = 'completed' | 'error' | 'cancelled' | 'skipped'
interface ArchiveResult {
  mappingId: number
  status: ArchiveStatus
  archived: number
  message?: string
}

const canArchive = computed(() => {
  const count = selectedMappings.value.length

  return count >= 1 && count <= 10
})

const archiveDisabledReason = computed(() => {
  const count = selectedMappings.value.length
  if (count === 0) return '선택된 매핑이 없습니다'
  if (count > 10) return '최대 10개까지 선택 가능합니다'

  return ''
})

function sleep(ms: number) {
  return new Promise<void>(resolve => setTimeout(resolve, ms))
}

interface ArchiveOneState {
  processed: number
  total: number | null
}

// 단일 매핑 chunk 루프.
// 반환값: 최종 결과 (results 에 push 할 레코드).
// 쓰기 부수 효과: 진행률 다이얼로그 갱신.
async function archiveOneMapping(
  idx: number,
  m: TargetItemMap,
  completedCount: number,
): Promise<ArchiveResult> {
  const state: ArchiveOneState = { processed: 0, total: null }

  const reportProgress = () => {
    archiveDialogRef.value?.setProgress({
      currentMappingIndex: idx,
      mapping: m,
      processed: state.total !== null ? Math.min(state.processed, state.total) : state.processed,
      total: state.total ?? 0,
      completedMappings: completedCount,
    })
  }

  reportProgress()

  try {
    while (true) {
      const res = await uploadHistoryApi.archive(m.client_id, {
        config_id: m.config_id,
        target_item_code: m.target_item_code,
        client_item_code: m.client_item_code,
        confirm_text: '위험성을 알고 동의합니다',
      })

      if (!res.success || !res.data) {
        return {
          mappingId: m.id,
          status: 'error',
          archived: state.processed,
          message: getErrorMessage(res.error, '이관 이력 삭제에 실패했습니다.'),
        }
      }

      const d = res.data

      // total·processed 는 archived + duplicate 를 모두 포함해 재호출·부분복구 케이스에서도 진행률이 전진하도록.
      // duplicate_count 는 이미 archive 에 있던 원본을 재처리한 건수 (C-1 멱등성 경로).
      if (state.total === null)
        state.total = d.archived_count + d.duplicate_count + d.remaining_count
      state.processed += d.archived_count + d.duplicate_count
      reportProgress()

      if (!d.has_more)
        return { mappingId: m.id, status: 'completed', archived: state.processed }

      if (archiveCancelled.value)
        return { mappingId: m.id, status: 'cancelled', archived: state.processed }

      await sleep(300)

      if (archiveCancelled.value)
        return { mappingId: m.id, status: 'cancelled', archived: state.processed }
    }
  }
  catch (e) {
    return {
      mappingId: m.id,
      status: 'error',
      archived: state.processed,
      message: (e as Error).message || 'network error',
    }
  }
}

async function runArchive() {
  if (archiveRunning.value) return

  const selected = [...selectedMappings.value]
  const results: ArchiveResult[] = []

  archiveRunning.value = true
  archiveCancelled.value = false

  try {
    for (const [idx, m] of selected.entries()) {
      if (archiveCancelled.value) {
        results.push({ mappingId: m.id, status: 'skipped', archived: 0 })
        continue
      }

      // 전체 진행 카운트: 처리된 매핑 개수 (성공·실패·취소·건너뜀 모두 포함).
      // 진행 바 고착을 막고 "처리가 얼마나 진행되었나" 를 정확히 반영.
      const processedMappings = results.length
      const result = await archiveOneMapping(idx, m, processedMappings)

      results.push(result)
    }

    showArchiveSummary(results, archiveCancelled.value)
  }
  finally {
    archiveRunning.value = false
    archiveDialog.value = false
  }
}

function showArchiveSummary(results: ArchiveResult[], wasCancelled: boolean) {
  const completed = results.filter(r => r.status === 'completed').length
  const errors = results.filter(r => r.status === 'error').length
  const partialCancelled = results.filter(r => r.status === 'cancelled').length
  const skipped = results.filter(r => r.status === 'skipped').length

  if (wasCancelled) {
    snackbar.info(
      `취소됨. 완료 ${completed}개 / 취소 ${partialCancelled}개 / 건너뜀 ${skipped}개 / 에러 ${errors}개`,
    )
  }
  else if (errors > 0) {
    snackbar.error(`완료 ${completed}개 / 에러 ${errors}개`)
  }
  else {
    snackbar.success(`이관 이력 삭제 완료 (${completed}개 매핑)`)
  }
}
</script>

<template>
  <VRow>
    <!-- 고객사 선택 -->
    <VCol
      cols="12"
      md="3"
    >
      <ClientSelector />
    </VCol>

    <VCol
      cols="12"
      md="4"
    >
      <VCard>
        <VCardText>
          <VRow>
            <VCol cols="6">
              <div class="d-flex align-center">
                <VAvatar
                  color="info"
                  variant="tonal"
                  size="48"
                  class="me-3"
                >
                  <VIcon
                    icon="tabler-layers-intersect"
                    size="24"
                  />
                </VAvatar>
                <div>
                  <VChip
                    v-if="clientCollectedReviewsCount === null"
                    size="small"
                    color="secondary"
                  >
                    계산 필요
                  </VChip>
                  <h4
                    v-else
                    class="text-h6 font-weight-bold mb-0"
                  >
                    {{ formatNumber(clientCollectedReviewsCount) }}
                  </h4>
                  <span class="text-body-2 text-disabled">수집된 리뷰 수</span>
                </div>
              </div>
            </VCol>
            <VCol cols="6">
              <div class="d-flex align-center">
                <VAvatar
                  color="success"
                  variant="tonal"
                  size="48"
                  class="me-3"
                >
                  <VIcon
                    icon="tabler-transfer"
                    size="24"
                  />
                </VAvatar>
                <div>
                  <VChip
                    v-if="clientFilteredReviewsCount === null"
                    size="small"
                    color="secondary"
                  >
                    계산 필요
                  </VChip>
                  <h4
                    v-else
                    class="text-h6 font-weight-bold mb-0"
                  >
                    {{ formatNumber(clientFilteredReviewsCount) }}
                  </h4>
                  <span class="text-body-2 text-disabled">이관 예상 리뷰 수</span>
                </div>
              </div>
            </VCol>
          </VRow>
        </VCardText>
      </VCard>
    </VCol>

    <VCol
      cols="12"
      md="4"
    >
      <VCard title="채널 별 리뷰 수">
        <VCardText class="pa-0">
          <VTable density="compact">
            <thead>
              <tr>
                <th>채널</th>
                <th>수집된 리뷰</th>
                <th>이관 예상</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="channel in reviewCountByChannels"
                :key="channel.channel_name"
              >
                <td>{{ channel.channel_name }}</td>
                <td>
                  <VChip
                    v-if="channel.collected_review_count === null"
                    size="x-small"
                    color="secondary"
                  >
                    계산 필요
                  </VChip>
                  <span v-else>{{ formatNumber(channel.collected_review_count) }}</span>
                </td>
                <td>
                  <VChip
                    v-if="channel.filtered_review_count === null"
                    size="x-small"
                    color="secondary"
                  >
                    계산 필요
                  </VChip>
                  <span v-else>{{ formatNumber(channel.filtered_review_count) }}</span>
                </td>
              </tr>
            </tbody>
          </VTable>
        </VCardText>
      </VCard>
    </VCol>

    <!-- 매핑 목록 -->
    <VCol cols="12">
      <VCard>
        <VCardText>
          <!-- 툴바 -->
          <VRow class="mb-4">
            <VCol
              cols="12"
              md="2"
            >
              <VTextField
                v-model="search"
                placeholder="상품코드 검색"
                density="compact"
                hide-details
                prepend-inner-icon="tabler-search"
              />
            </VCol>
            <VCol
              cols="12"
              md="7"
            >
              <div class="d-flex flex-column gap-2">
                <!--
                  데이터 작업 그룹 (자주 쓰이는 안전한 액션). 매핑 삭제는 이
                  그룹의 끝에 드롭다운으로만 노출하며, Target 작업 그룹과는
                  별도 줄에 분리해 시선/클릭 동선을 떼어낸다. 2026-04-07 사고 (발생) / 2026-04-08 복원 완료
                  (Client 1538 매핑 93건 실수 일괄 삭제) 참고. 
                -->
                <div class="d-flex flex-wrap gap-2">
                  <!-- 상품명 수집 -->
                  <VMenu>
                    <template #activator="{ props }">
                      <VBtn
                        v-bind="props"
                        variant="outlined"
                        color="primary"
                        :disabled="driverConfigs.length === 0"
                      >
                        상품명 수집
                        <VIcon
                          end
                          icon="tabler-chevron-down"
                        />
                      </VBtn>
                    </template>
                    <VList>
                      <VListItem @click="resolveClientProductNames">
                        전체 매핑 자사몰 상품명 수집
                      </VListItem>
                      <VListItem @click="resolveTargetProductNames">
                        전체 매핑 채널 상품명 수집
                      </VListItem>
                      <VDivider />
                      <VListItem
                        :disabled="selectedMappings.length === 0"
                        @click="resolveClientProductNamesForMappings"
                      >
                        선택된 매핑({{ selectedMappings.length }}) 자사몰 상품명 수집
                      </VListItem>
                      <VListItem
                        :disabled="selectedMappings.length === 0"
                        @click="resolveTargetProductNamesForMappings"
                      >
                        선택된 매핑({{ selectedMappings.length }}) 채널 상품명 수집
                      </VListItem>
                      <VDivider />
                      <VListItem
                        v-for="config in driverConfigs"
                        :key="config.id"
                        @click="resolveTargetProductNameByDriverConfigId(config.id, config.download_driver?.name || config.name)"
                      >
                        <span class="text-primary">{{ config.download_driver?.name || config.name }}</span> 상품명 수집
                      </VListItem>
                    </VList>
                  </VMenu>

                  <!-- 자사몰 상품명 초기화 -->
                  <VMenu>
                    <template #activator="{ props }">
                      <VBtn
                        v-bind="props"
                        variant="outlined"
                        prepend-icon="mdi-restore"
                        :disabled="!selectedClient"
                      >
                        자사몰 상품명 초기화
                        <VIcon
                          end
                          icon="tabler-chevron-down"
                        />
                      </VBtn>
                    </template>
                    <VList>
                      <VListItem
                        :disabled="selectedMappings.length === 0"
                        @click="resetClientProductNamesForMappings"
                      >
                        <VListItemTitle>선택된 {{ selectedMappings.length }}건</VListItemTitle>
                      </VListItem>
                      <VListItem @click="resetClientProductNames">
                        <VListItemTitle>전체 매핑</VListItemTitle>
                      </VListItem>
                    </VList>
                  </VMenu>

                  <!-- 자사몰 상품명 강제 수집 -->
                  <VMenu>
                    <template #activator="{ props }">
                      <VBtn
                        v-bind="props"
                        variant="outlined"
                        color="warning"
                        prepend-icon="mdi-reload-alert"
                        :disabled="!selectedClient"
                      >
                        자사몰 상품명 강제 수집
                        <VIcon
                          end
                          icon="tabler-chevron-down"
                        />
                      </VBtn>
                    </template>
                    <VList>
                      <VListItem
                        :disabled="selectedMappings.length === 0"
                        @click="forceResolveClientProductNamesForMappings"
                      >
                        <VListItemTitle>선택된 {{ selectedMappings.length }}건</VListItemTitle>
                      </VListItem>
                      <VListItem @click="forceResolveClientProductNames">
                        <VListItemTitle>전체 매핑</VListItemTitle>
                      </VListItem>
                    </VList>
                  </VMenu>

                  <!-- 유사도 생성 -->
                  <VMenu>
                    <template #activator="{ props }">
                      <VBtn
                        v-bind="props"
                        variant="outlined"
                        color="primary"
                      >
                        유사도 생성
                        <VIcon
                          end
                          icon="tabler-chevron-down"
                        />
                      </VBtn>
                    </template>
                    <VList>
                      <VListItem @click="buildSimilarity">
                        전체 매핑 유사도 생성
                      </VListItem>
                      <VDivider />
                      <VListItem
                        :disabled="selectedMappings.length === 0"
                        @click="buildSimilarityForMappings"
                      >
                        선택된 매핑({{ selectedMappings.length }}) 유사도 생성
                      </VListItem>
                    </VList>
                  </VMenu>

                  <!-- 리뷰 수집 -->
                  <VMenu>
                    <template #activator="{ props }">
                      <VBtn
                        v-bind="props"
                        variant="outlined"
                        color="primary"
                      >
                        리뷰 수집
                        <VIcon
                          end
                          icon="tabler-chevron-down"
                        />
                      </VBtn>
                    </template>
                    <VList>
                      <VListItem @click="collectReviews">
                        전체 매핑 리뷰 수집
                      </VListItem>
                      <VDivider />
                      <VListItem
                        :disabled="selectedMappings.length === 0"
                        @click="collectReviewsForMappings"
                      >
                        선택된 매핑({{ selectedMappings.length }}) 리뷰 수집
                      </VListItem>
                      <VDivider />
                      <VListItem
                        v-for="config in driverConfigs"
                        :key="`collect-review-${config.id}`"
                        @click="collectReviewsByDriverConfig(config.id, config.download_driver?.name || config.name)"
                      >
                        <span class="text-primary">{{ config.download_driver?.name || config.name }}</span> 리뷰 수집
                      </VListItem>
                    </VList>
                  </VMenu>

                  <VBtn
                    variant="outlined"
                    color="primary"
                    @click="openCreateDialog"
                  >
                    매핑 데이터 추가
                  </VBtn>

                  <VBtn
                    color="primary"
                    :loading="isExcelDownloading"
                    @click="downloadExcelExport"
                  >
                    엑셀 다운로드
                  </VBtn>

                  <VBtn
                    variant="outlined"
                    color="info"
                    :disabled="!selectedClient"
                    @click="updateExpectedReviewCount"
                  >
                    리뷰 수 계산
                  </VBtn>

                  <!--
                    매핑 정보 삭제 (위험 액션).
                    WARNING: 반드시 드롭다운 안에 유지할 것. 단일 버튼으로
                    노출하면 다른 자주 쓰는 버튼과 인접해 실수 클릭이 발생한다
                    (2026-04-07 사고 (발생) / 2026-04-08 복원 완료 참조). 전체 삭제는 추가로 업체명 타이핑
                    확인이 필수. 
                  -->
                  <VMenu>
                    <template #activator="{ props }">
                      <VBtn
                        v-bind="props"
                        variant="outlined"
                        color="error"
                        :disabled="!selectedClient"
                      >
                        매핑 정보 삭제
                        <VIcon
                          end
                          icon="tabler-chevron-down"
                        />
                      </VBtn>
                    </template>
                    <VList>
                      <VListItem
                        :disabled="selectedMappings.length === 0"
                        @click="openDeleteSelectedDialog"
                      >
                        선택된 매핑({{ selectedMappings.length }}) 삭제
                      </VListItem>
                      <VDivider />
                      <VListItem
                        class="text-error"
                        @click="openDeleteAllDialog"
                      >
                        업체 매핑 전체 삭제
                      </VListItem>
                    </VList>
                  </VMenu>

                  <!--
                    이관 이력 삭제 (Danger Zone).
                    선택된 매핑(1~10개) 의 MongoDB review_upload_target_list 레코드를
                    archive 컬렉션으로 이동. 중복 이관 위험이 있으므로 다이얼로그에서
                    확인 문구 타이핑 후에만 실행된다.
                  -->
                  <VBtn
                    color="error"
                    variant="tonal"
                    prepend-icon="tabler-archive"
                    :disabled="!canArchive || archiveRunning"
                    @click="archiveDialog = true"
                  >
                    이관 이력 삭제
                    <VTooltip
                      v-if="!canArchive"
                      activator="parent"
                      location="bottom"
                    >
                      {{ archiveDisabledReason }}
                    </VTooltip>
                  </VBtn>
                </div>

                <!--
                  Target 작업 그룹 (특수/위험 액션). 데이터 작업 그룹과 별도
                  줄에 두어 매핑 삭제와 시선/클릭 동선이 겹치지 않도록 한다. 
                -->
                <div class="d-flex flex-wrap gap-2">
                  <!-- Target 액션 -->
                  <VMenu>
                    <template #activator="{ props }">
                      <VBtn
                        v-bind="props"
                        variant="outlined"
                        color="warning"
                        :disabled="!selectedClient || targetActionLoading !== null"
                      >
                        Target 액션
                        <VIcon
                          end
                          icon="tabler-chevron-down"
                        />
                      </VBtn>
                    </template>
                    <VList>
                      <VListItem
                        :disabled="targetActionLoading !== null"
                        @click="clearItemTitleAll"
                      >
                        전체 매핑 상품명 초기화
                      </VListItem>
                      <VListItem
                        :disabled="targetActionLoading !== null"
                        @click="deleteZzExpressAll"
                      >
                        전체 매핑 직진배송 삭제
                      </VListItem>
                      <VDivider />
                      <VListItem
                        :disabled="selectedMappings.length === 0 || targetActionLoading !== null"
                        @click="clearItemTitleForMappings"
                      >
                        선택된 매핑({{ selectedMappings.length }}) 상품명 초기화
                      </VListItem>
                      <VListItem
                        :disabled="selectedMappings.length === 0 || targetActionLoading !== null"
                        @click="deleteZzExpressForMappings"
                      >
                        선택된 매핑({{ selectedMappings.length }}) 직진배송 삭제
                      </VListItem>
                    </VList>
                  </VMenu>
                </div>
              </div>
            </VCol>
            <VCol
              cols="12"
              md="3"
              class="d-flex justify-end align-center gap-3"
            >
              <span class="text-body-2">총 {{ formatNumber(totalMapCount) }}개 제품</span>
              <VSelect
                v-model="selectedDriverConfigId"
                :items="driverConfigOptions"
                item-title="title"
                item-value="value"
                density="compact"
                hide-details
                style="max-width: 200px;"
              />
            </VCol>
          </VRow>

          <!-- 데이터 테이블 -->
          <VDataTableServer
            v-model="selectedMappings"
            v-model:items-per-page="itemsPerPage"
            v-model:page="page"
            v-model:sort-by="sortBy"
            :headers="headers"
            :items="mappings"
            :items-length="totalMapCount"
            :loading="loading"
            show-select
            item-value="id"
            return-object
            density="compact"
            class="text-no-wrap"
            @update:page="handlePageChange"
            @update:sort-by="handleSortChange"
          >
            <template #item.target_item_name="{ item }">
              <div>
                <span v-if="item.target_item_name_resolved === 1">
                  <strong>{{ item.target_item_name }}</strong>
                </span>
                <VChip
                  v-else-if="item.target_item_name_resolved === null"
                  size="x-small"
                  color="secondary"
                >
                  상품명 수집 전
                </VChip>
                <VChip
                  v-else
                  size="x-small"
                  color="error"
                >
                  수집 오류
                </VChip>
              </div>
              <div>
                <a
                  v-if="item.target_item_url"
                  :href="item.target_item_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-primary"
                >
                  {{ item.target_item_code }}
                </a>
                <span
                  v-else
                  class="text-body-2"
                >{{ item.target_item_code }}</span>
              </div>
            </template>

            <template #item.client_item_name="{ item }">
              <div>
                <span v-if="item.client_item_name_resolved === 1">
                  <strong>{{ item.client_item_name }}</strong>
                </span>
                <VChip
                  v-else-if="item.client_item_name_resolved === null"
                  size="x-small"
                  color="secondary"
                >
                  상품명 수집 전
                </VChip>
                <VChip
                  v-else
                  size="x-small"
                  color="error"
                >
                  수집 오류
                </VChip>
              </div>
              <div>
                <a
                  v-if="item.client_item_url"
                  :href="item.client_item_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-primary"
                >
                  {{ item.client_item_code }}
                </a>
                <span
                  v-else
                  class="text-body-2"
                >{{ item.client_item_code }}</span>
              </div>
            </template>

            <template #item.similarity="{ item }">
              <VChip
                v-if="item.similarity === null"
                size="x-small"
                color="secondary"
              >
                생성 전
              </VChip>
              <span v-else>{{ formatNumber(item.similarity) }}</span>
            </template>

            <template #item.collected_review_count="{ item }">
              <VChip
                v-if="item.collected_review_count === null"
                size="x-small"
                color="secondary"
              >
                수집 전
              </VChip>
              <span v-else>{{ formatNumber(item.collected_review_count) }}</span>
            </template>

            <template #item.filtered_review_count="{ item }">
              <VChip
                v-if="item.filtered_review_count === null"
                size="x-small"
                color="secondary"
              >
                수집 전
              </VChip>
              <span v-else>{{ formatNumber(item.filtered_review_count) }}</span>
            </template>

            <template #item.duplicate_client_items="{ item }">
              {{ item.duplicate_client_items ?? '-' }}
            </template>

            <template #item.duplicate_target_items="{ item }">
              {{ item.duplicate_target_items ?? '-' }}
            </template>

            <template #item.actions="{ item }">
              <VMenu>
                <template #activator="{ props }">
                  <VBtn
                    v-bind="props"
                    icon
                    variant="text"
                    size="small"
                  >
                    <VIcon icon="tabler-dots-vertical" />
                  </VBtn>
                </template>
                <VList density="compact">
                  <VListItem @click="resolveTargetProductNameById(item.id)">
                    <template #prepend>
                      <VIcon
                        icon="tabler-circle"
                        size="16"
                      />
                    </template>
                    채널 상품명 수집
                  </VListItem>
                  <VListItem @click="resolveClientProductNameById(item.id)">
                    <template #prepend>
                      <VIcon
                        icon="tabler-triangle"
                        size="16"
                      />
                    </template>
                    자사몰 상품명 수집
                  </VListItem>
                  <VDivider />
                  <VListItem @click="collectReviewsForMapping(item.id)">
                    <template #prepend>
                      <VIcon
                        icon="tabler-square"
                        size="16"
                      />
                    </template>
                    리뷰 수집
                  </VListItem>
                  <VDivider />
                  <VListItem
                    :disabled="!item.target_id || targetActionLoading !== null"
                    @click="clearItemTitle(item)"
                  >
                    <template #prepend>
                      <VIcon
                        icon="tabler-eraser"
                        size="16"
                      />
                    </template>
                    상품명 초기화
                  </VListItem>
                  <VListItem
                    :disabled="!item.target_id || targetActionLoading !== null"
                    @click="deleteZzExpress(item)"
                  >
                    <template #prepend>
                      <VIcon
                        icon="tabler-truck-off"
                        size="16"
                      />
                    </template>
                    직진배송 삭제
                  </VListItem>
                  <VListItem
                    :disabled="targetActionLoading !== null"
                    @click="generateSimilarityForMapping(item)"
                  >
                    <template #prepend>
                      <VIcon
                        icon="tabler-percentage"
                        size="16"
                      />
                    </template>
                    유사도 계산
                  </VListItem>
                  <VDivider />
                  <VListItem @click="openEditDialog(item)">
                    <template #prepend>
                      <VIcon
                        icon="tabler-edit"
                        size="16"
                      />
                    </template>
                    수정
                  </VListItem>
                  <VListItem @click="deleteMapping(item)">
                    <template #prepend>
                      <VIcon
                        icon="tabler-trash"
                        size="16"
                      />
                    </template>
                    삭제
                  </VListItem>
                </VList>
              </VMenu>
            </template>
          </VDataTableServer>
        </VCardText>
      </VCard>
    </VCol>

    <!-- 확인 다이얼로그 -->
    <VDialog
      v-model="confirmDialog"
      max-width="400"
      :persistent="confirmActionExecuting"
    >
      <VCard>
        <VCardTitle>확인</VCardTitle>
        <VCardText>{{ confirmMessage }}</VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="outlined"
            :disabled="confirmActionExecuting"
            @click="confirmDialog = false"
          >
            취소
          </VBtn>
          <VBtn
            color="primary"
            :loading="confirmActionExecuting"
            :disabled="confirmActionExecuting"
            @click="executeConfirmAction"
          >
            확인
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- 매핑 수정 다이얼로그 -->
    <VDialog
      v-model="editDialog"
      max-width="500"
    >
      <VCard>
        <VCardTitle>매핑 수정</VCardTitle>
        <VCardText>
          <VTextField
            v-model="mappingForEdit.target_item_code"
            label="채널 아이템 코드"
            class="mb-4"
          />
          <VTextField
            v-model="mappingForEdit.client_item_code"
            label="자사몰 아이템 코드"
          />
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="outlined"
            @click="editDialog = false"
          >
            취소
          </VBtn>
          <VBtn
            color="primary"
            @click="updateMapping"
          >
            저장
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- 매핑 추가 다이얼로그 -->
    <VDialog
      v-model="createDialog"
      max-width="800"
    >
      <VCard>
        <VCardTitle>매핑 추가</VCardTitle>
        <VCardText>
          <VRow class="mb-2">
            <VCol cols="4">
              <span class="text-body-2 font-weight-medium">채널</span>
            </VCol>
            <VCol>
              <span class="text-body-2 font-weight-medium">채널 아이템 코드</span>
            </VCol>
            <VCol>
              <span class="text-body-2 font-weight-medium">자사몰 아이템 코드</span>
            </VCol>
            <VCol cols="1" />
          </VRow>

          <VRow
            v-for="(mapping, index) in mappingsForCreate"
            :key="index"
            class="mb-2"
          >
            <VCol cols="4">
              <VSelect
                v-model="mapping.config_id"
                :items="addableChannels"
                item-title="title"
                item-value="value"
                density="compact"
                hide-details
                placeholder="채널 선택"
              />
            </VCol>
            <VCol>
              <VTextField
                v-model="mapping.target_item_code"
                density="compact"
                hide-details
                placeholder="채널 아이템 코드"
              />
            </VCol>
            <VCol>
              <VTextField
                v-model="mapping.client_item_code"
                density="compact"
                hide-details
                placeholder="자사몰 아이템 코드"
              />
            </VCol>
            <VCol cols="1">
              <VBtn
                icon
                variant="outlined"
                color="error"
                size="small"
                :disabled="index === 0"
                @click="removeCreateMappingInput(index)"
              >
                <VIcon icon="tabler-x" />
              </VBtn>
            </VCol>
          </VRow>

          <VRow>
            <VCol>
              <VBtn
                block
                variant="outlined"
                color="primary"
                @click="addCreateMappingInput"
              >
                <VIcon
                  icon="tabler-plus"
                  class="me-2"
                />
                행 추가
              </VBtn>
            </VCol>
          </VRow>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="outlined"
            @click="createDialog = false"
          >
            취소
          </VBtn>
          <VBtn
            color="primary"
            @click="storeMapping"
          >
            저장
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- 선택된 매핑 삭제 확인 다이얼로그 -->
    <VDialog
      v-model="deleteSelectedDialog"
      max-width="500"
      persistent
    >
      <VCard>
        <VCardTitle class="text-h6">
          선택된 매핑 삭제
        </VCardTitle>
        <VCardText>
          <p class="text-body-2 mb-3">
            선택한 매핑 <strong>{{ selectedMappings.length }}건</strong>이 삭제됩니다.
            삭제 이력은 감사 테이블에 보관되어 운영자가 복원할 수 있습니다.
          </p>
          <VTextField
            v-model="deleteSelectedReason"
            label="삭제 사유 (선택, 최대 500자)"
            density="compact"
            counter="500"
            :maxlength="500"
          />
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            :disabled="isDeletingSelected"
            @click="deleteSelectedDialog = false"
          >
            취소
          </VBtn>
          <VBtn
            color="error"
            :loading="isDeletingSelected"
            :disabled="isDeletingSelected || selectedMappings.length === 0"
            @click="confirmDeleteSelectedMappings"
          >
            {{ selectedMappings.length }}건 삭제
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- 업체 매핑 정보 삭제 확인 다이얼로그 -->
    <VDialog
      v-model="deleteAllDialog"
      max-width="540"
      persistent
    >
      <VCard>
        <VCardTitle class="text-h6">
          매핑 전체 삭제 확인
        </VCardTitle>
        <VCardText>
          <VAlert
            type="error"
            variant="tonal"
            class="mb-4"
          >
            <strong>{{ selectedClient?.name }}</strong>의 모든 매핑이 영구 삭제됩니다.
            삭제 이력은 감사 테이블에 보관되며, 운영자가 별도로 복원할 수 있습니다.
          </VAlert>
          <p class="text-body-2 mb-2">
            계속하려면 업체명 <strong>{{ selectedClient?.name }}</strong>을(를) 정확히 입력하세요.
          </p>
          <VTextField
            v-model="deleteAllConfirmName"
            :label="`업체명 입력: ${selectedClient?.name ?? ''}`"
            density="compact"
            autocomplete="off"
            class="mb-2"
          />
          <VTextField
            v-model="deleteAllReason"
            label="삭제 사유 (선택, 최대 500자)"
            density="compact"
            counter="500"
            :maxlength="500"
          />
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            :disabled="isDeletingAll"
            @click="deleteAllDialog = false"
          >
            취소
          </VBtn>
          <VBtn
            color="error"
            :disabled="!canConfirmDeleteAll"
            :loading="isDeletingAll"
            @click="confirmDeleteAllMappings"
          >
            삭제 실행
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- 이관 이력 삭제 다이얼로그 (Danger Zone) -->
    <ArchiveUploadHistoryDialog
      ref="archiveDialogRef"
      v-model="archiveDialog"
      :selected-mappings="selectedMappings"
      @confirm="runArchive"
      @cancel="archiveCancelled = true"
    />

    <!-- 스낵바 -->
    <VSnackbar
      v-model="snackbar.isVisible.value"
      :color="snackbar.color.value"
      :timeout="snackbar.timeout.value"
    >
      {{ snackbar.message.value }}
    </VSnackbar>
  </VRow>
</template>

<style scoped>
.v-data-table :deep(tbody tr) {
  cursor: pointer;
}
</style>
